import { useRef, useState } from "react";

/** 10MB, matching what the catalogue route's multer limit accepts. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export interface ImageUploadState {
	/** How many files are still in flight. */
	uploading: number;
	/** Filenames that did not make it, with the reason where there is one. */
	rejected: string[];
	/** True when the last run put at least one image up. */
	addedAny: boolean;
}

/**
 * Uploading a set of images, minus any opinion about where they go.
 *
 * Plan 008 asks for an `ImageUploader` primitive. The part worth extracting is
 * not the markup -- a file input and a grid of thumbnails are cheap -- it is the
 * POLICY underneath, which `product-media` worked out the hard way and which any
 * second uploader would otherwise get wrong in the same three places:
 *
 * ONE AT A TIME, NOT AS A BATCH. `POST /upload/multiple` takes ten and is
 * all-or-nothing: one rejected file loses the other nine, so an operator who
 * picked ten photographs of a tile re-picks nine of them because the tenth was
 * a HEIC. This keeps whatever succeeded.
 *
 * SIZE CHECKED LOCALLY TOO. The server enforces it, but sending a 40MB file
 * anyway costs the operator the upload and returns a generic failure; refusing
 * it here names the file and the reason at once.
 *
 * THE INPUT IS CLEARED AFTER EVERY RUN. Without it, picking the same file twice
 * in a row fires no change event and the second attempt looks like it did
 * nothing.
 *
 * `upload` is the caller's, which is the whole point of the split: products post
 * to the catalogue route, and whatever comes next will not.
 */
export function useImageUpload(upload: (file: File) => Promise<string>): {
	state: ImageUploadState;
	inputRef: React.RefObject<HTMLInputElement | null>;
	/** Hand it the picked files; get back the URLs that made it. */
	run: (files: FileList | File[]) => Promise<string[]>;
} {
	const inputRef = useRef<HTMLInputElement>(null);
	const [state, setState] = useState<ImageUploadState>({
		uploading: 0,
		rejected: [],
		addedAny: false,
	});

	async function run(files: FileList | File[]): Promise<string[]> {
		const chosen = [...files];
		if (chosen.length === 0) return [];

		setState({ uploading: chosen.length, rejected: [], addedAny: false });

		const uploaded: string[] = [];
		const rejected: string[] = [];
		for (const file of chosen) {
			if (file.size > MAX_IMAGE_BYTES) {
				rejected.push(`${file.name} (over 10MB)`);
				setState((s) => ({ ...s, uploading: s.uploading - 1 }));
				continue;
			}
			try {
				uploaded.push(await upload(file));
			} catch {
				rejected.push(file.name);
			}
			setState((s) => ({ ...s, uploading: s.uploading - 1 }));
		}

		setState({
			uploading: 0,
			rejected,
			addedAny: uploaded.length > 0,
		});
		if (inputRef.current) inputRef.current.value = "";
		return uploaded;
	}

	return { state, inputRef, run };
}

/**
 * Reordering an image list where position IS the meaning.
 *
 * `catalogProducts.images` holds `{url, type, alt}` objects and still has NO
 * primary flag: the first image is the primary one, so promoting means moving
 * to index 0 rather than setting a field. That was true when the column held
 * bare URLs and the promotion deliberately kept it true.
 *
 * GENERIC over the element type, because the column's shape changed once and
 * these are position helpers that never look inside an entry. Typed to
 * `string[]` they would have silently stopped being usable for the thing they
 * are named after. Kept beside the upload policy because the two are only ever
 * used together, and because a caller reimplementing "promote" as a sort would
 * silently change which image is primary.
 */
export function promoteImage<T>(images: T[], index: number): T[] {
	if (index <= 0 || index >= images.length) return images;
	const next = [...images];
	const [moved] = next.splice(index, 1);
	next.unshift(moved);
	return next;
}

export function removeImage<T>(images: T[], index: number): T[] {
	return images.filter((_image, i) => i !== index);
}
